import { Button } from 'anf-core-react';
import classNames from 'classnames';
import {
  arrayOf, bool, func, string, number, shape,
} from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import useTranslatedText from '../../hooks/useTranslatedText';
import { FACET_DICTIONARY } from '../../tools/constants';
import $document from '../../tools/document';
import BreakpointProvider from '../BreakpointProvider';
import ClearAllButton from '../ClearAllButton/ClearAllButton';
import FacetAndCategoryToaster from '../FacetAndCategoryToaster';
import Loader from '../Loader';
import style from './FacetToaster.module.scss';
import FilterButtons from './FilterButtons';

/**
 * FacetToaster component displays a modal toaster for selecting and applying facet filters.
 * It slides from bottom/right of the screen and allows users to select options, clear selections,
 * and apply filters to update search results.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} [props.facetId=''] - The ID of the facet being filtered.
 * @param {string} [props.facetName=''] - The display name of the facet.
 * @param {boolean} [props.loading=false] - Whether the filter results are loading.
 * @param {Function} [props.onFacetToasterChange=() => {}] - Callback when facet selection changes.
 * @param {Array<Object>} [props.options=[]] - List of filter options to display.
 * @param {string} props.options[].name - Name of the filter option.
 * @param {number} props.options[].count - Count of items for the filter option.
 * @param {boolean} props.options[].enabled - Whether the filter option is enabled.
 * @param {number} [props.resultsCount=0] - Number of results for the current filter selection.
 * @param {Function} props.setToasterState - Function to update the toaster state.
 * @param {Object} props.toasterState - Current state of the toaster.
 * @param {number} props.toasterState.count - Current count of selected results.
 * @param {boolean} props.toasterState.isOpen - Whether the toaster is open.
 * @param {Array<string>} props.toasterState.selectedFilters - Currently selected filter names.
 *
 * @returns {JSX.Element} The rendered FacetToaster component.
 */
const FacetToaster = ({
  facetId = '',
  facetName = '',
  loading = false,
  onFacetToasterChange = () => { },
  options = [],
  resultsCount = 0,
  setToasterState,
  toasterState,
}) => {
  const isSizeFacet = facetId === 'sizes' || facetId === FACET_DICTIONARY.sizes;
  const showResultsText = useTranslatedText(
    'showResults',
    {
      fallback: `Show ${resultsCount || 'All'} Results`,
      replacements: [(resultsCount || 'All').toString()],
    },
  );

  const [footerWidth, setFooterWidth] = useState('');

  useEffect(() => {
    const toasterModalContentWidth = $document.querySelector('#category-facet-toaster .modal-content')?.offsetWidth;
    setFooterWidth(`${toasterModalContentWidth}px`);
  }, []);

  const clearSelections = useCallback(() => {
    onFacetToasterChange(facetId, [], 'drawer');
    setToasterState((prevState) => ({
      ...prevState,
      count: resultsCount,
      selectedFilters: [],
    }));
  }, [facetId, onFacetToasterChange, resultsCount, setToasterState]);

  const handleShowResults = useCallback(() => {
    setToasterState((prevState) => ({ ...prevState, isOpen: false }));
  }, [setToasterState]);

  const onOptionButtonClick = useCallback(async (buttonCount, name) => {
    const isAlreadySelected = toasterState.selectedFilters.includes(name);
    let updatedSelectedFilters = [...toasterState.selectedFilters, name];
    let includeButtonCount = false;

    if (isAlreadySelected) {
      updatedSelectedFilters = toasterState.selectedFilters.filter((value) => value !== name);
    } else {
      includeButtonCount = true;
    }
    onFacetToasterChange(facetId, updatedSelectedFilters, 'drawer');
    setToasterState((prevState) => ({
      ...prevState,
      selectedFilters: updatedSelectedFilters,
      count: includeButtonCount ? buttonCount : prevState.count,
    }));
  }, [toasterState.selectedFilters, onFacetToasterChange, facetId, setToasterState]);

  return (
    <BreakpointProvider>
      <FacetAndCategoryToaster
        footer={[
          <div key="facet-footer" className={classNames('scope-1892', style.footerFacetToaster)} style={{ width: footerWidth }}>
            <Button
              classList={style.showResultsButton}
              isFullWidth
              onClick={handleShowResults}
              type="submit"
              variant="secondary"
            >
              {loading ? <Loader variant="inverse-dots" /> : showResultsText?.value}
            </Button>
            <ClearAllButton
              isBordered
              onClick={clearSelections}
            />
          </div>,
        ]}
        setToasterState={setToasterState}
        toasterHeadingLabel={facetName}
        toasterState={toasterState}
      >
        <div className={classNames('scope-1892', style.optionButtonWrapper, { [style.sizes]: isSizeFacet })}>
          <FilterButtons
            filters={options}
            onOptionButtonClick={onOptionButtonClick}
            selectedFilters={toasterState.selectedFilters}
            size={isSizeFacet ? 'default' : 'long'}
          />
        </div>
      </FacetAndCategoryToaster>
    </BreakpointProvider>
  );
};

FacetToaster.propTypes = {
  facetId: string,
  facetName: string,
  filter: string,
  loading: bool,
  onFacetToasterChange: func,
  resultsCount: number,
  options: arrayOf(shape({
    count: number,
    name: string,
    enabled: bool,
  })),
  setToasterState: func,
  toasterState: shape({
    count: number,
    isOpen: bool,
    selectedFilters: arrayOf(string),
  }),
};

export default FacetToaster;
